import { JSX } from 'preact';

import cx from 'clsx';
import { Text } from '@paylink/constants';
import { PaylinkState } from '@paylink/types/paylink.types';

import { formatAmount } from '@shared/utils';
import { Image } from '@shared/components/core';

import QueriesCard from './QueriesCard';
import PaymentFor from '../paymentFor/PaymentFor.view';
import { formatExpiryDate } from './TerminalState.utils';
import type {
	ITerminalConfig,
	ITerminalStateProps,
} from './TerminalState.types';
import styles from './TerminalState.module.scss';

const STAMP_URLS = {
	paid: 'https://imgstatic.phonepe.com/images/mercury-ui/icons/601/601/paylinkStampPaid.png',

	expired:
		'https://imgstatic.phonepe.com/images/mercury-ui/icons/601/601/paylinkStampExpired.png',
	cancelled:
		'https://imgstatic.phonepe.com/images/mercury-ui/icons/601/601/paylinkStampCancelled.png',
};

const terminalConfig: Partial<Record<PaylinkState, ITerminalConfig>> = {
	[PaylinkState.COMPLETED]: {
		overline: Text.PAYMENT_REQUEST_FOR,
		tagLabel: Text.PAID_SUCCESSFULLY,
		tagClass: 'bg-success color-success',
		stamp: STAMP_URLS.paid,
	},
	[PaylinkState.CANCELLED]: {
		overline: Text.PAYMENT_LINK_FOR,
		tagLabel: Text.LINK_CANCELLED,
		tagClass: cx('grey6', styles.tagCancelled),
		stamp: STAMP_URLS.cancelled,
	},
	[PaylinkState.EXPIRED]: {
		overline: Text.PAYMENT_LINK_FOR,
		tagLabel: Text.LINK_EXPIRED,
		tagClass: 'bg-error color-required',
		stamp: STAMP_URLS.expired,
	},
};

const TerminalState = ({
	paylinkState,
	merchantName,
	amount,
	orderId,
	expireAt,
	description,
	isMobile,
}: ITerminalStateProps) => {
	const textSize = isMobile ? 'body' : 'h5';

	const renderDetails = (): JSX.Element | null => {
		if (paylinkState === PaylinkState.COMPLETED) {
			return (
				<div
					className={cx('d-flex', {
						'flex-column gap6': isMobile,
						[styles.detailColumns]: !isMobile,
					})}
				>
					<div className="d-flex flex-column gap1">
						<span className={cx('medium grey6 h6 lhp135', styles.overline)}>
							{Text.PAID_TO}
						</span>
						<span className={cx('bolder dark3 lhp130', textSize)}>
							{merchantName}
						</span>
					</div>

					{isMobile && description && (
						<PaymentFor description={description} isMobile />
					)}

					<div className="d-flex flex-column gap1">
						<span className={cx('medium grey6 h6 lhp135', styles.overline)}>
							{Text.TRANSACTION_ID}
						</span>
						<span
							className={
								(cx('lighter dark3 lhp130', textSize), styles.txnValue)
							}
						>
							{orderId}
						</span>
					</div>
				</div>
			);
		}

		if (paylinkState === PaylinkState.CANCELLED) {
			return (
				<div className="d-flex flex-column gap4">
					<p
						className={cx('lighter dark3 m0 ', textSize, {
							lhp130: !isMobile,
							lhp135: isMobile,
						})}
					>
						{Text.LINK_INVALID}
						<br />
						<span className="bolder">{merchantName}</span>
						{Text.CANCELLED_BY_SUFFIX}
					</p>
					{isMobile && description && (
						<PaymentFor description={description} isMobile />
					)}
				</div>
			);
		}

		if (paylinkState === PaylinkState.EXPIRED) {
			return (
				<div className="d-flex flex-column gap4">
					<div className="d-flex flex-column gap1">
						<span className={cx('medium grey6 body', styles.overline)}>
							{Text.LINK_CREATED_BY}
						</span>
						<span className={cx('bolder dark3 lhp130', textSize)}>
							{merchantName}
						</span>
						{expireAt && (
							<span
								className={cx(
									'lighter lhp135',
									isMobile ? 'dark3 body' : 'grey6 h5',
								)}
							>
								{Text.EXPIRED_ON} {formatExpiryDate(expireAt)}
							</span>
						)}
					</div>
					{isMobile && description && (
						<PaymentFor description={description} isMobile />
					)}
				</div>
			);
		}

		return null;
	};

	const config = terminalConfig[paylinkState];
	const details = renderDetails();

	if (!config || !details) return null;

	const stampClassName = cx('absolute', {
		[styles.stampMobile]: isMobile,
		[styles.stamp]: !isMobile,
	});

	return (
		<div className="d-flex flex-column width100 gap4">
			<div
				className={cx(
					'relative overflow-hidden bg-white border1 border-orange border-radius4 card-shadow1',
					styles.statusCard,
				)}
			>
				<div
					className={cx('d-flex flex-column gap6 relative', styles.content, {
						p6: !isMobile,
						'pt6 px4 pb4': isMobile,
					})}
				>
					<div className="d-flex flex-column gap1">
						<span className={cx('medium grey6 h6 lhp135', styles.overline)}>
							{config.overline}
						</span>
						<span className="bolder dark3 h2 lhp135 ">
							{formatAmount(amount)}
						</span>
						<span
							className={cx(
								'bolder lhp135 py1 px2 h6 width-fit-content border-radius1 mt3',
								styles.tag,
								config.tagClass,
							)}
						>
							{config.tagLabel}
						</span>
					</div>
					{details}
				</div>

				<Image src={config.stamp} className={stampClassName} alt="" />
			</div>

			<QueriesCard merchantName={merchantName} isMobile={isMobile} />
		</div>
	);
};

export default TerminalState;
